import {
  cioFacetsToUrlFilters,
  urlFiltersToCioFacets,
} from '~/grid/utils/cioUtils';

import { getNewQueryParamString, QueryParamBehavior } from './routing';

/**
 * Checks if the given facet name is a size facet.
 * Specific to Constructor data shape.
 *
 * @param {string} cioFacetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a size facet.
 */
export const isCioSizeFacet = (cioFacetName) => {
  return cioFacetName?.startsWith('available_size_');
};

/**
 * Checks if the given facet name is a color facet.
 * Specific to Constructor data shape.
 *
 * @param {string} cioFacetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a color facet.
 */
export const isCioColorFacet = (cioFacetName) => {
  return cioFacetName === 'tag_color_list';
};

/**
 * Updates the selected filters in the URL params, which we use as the source of truth for state management.
 * All data fetching is based on the URL params.
 *
 * @param {string} facetName - The name of the facet to update.
 * @param {string} facetOption - The option of the facet to update.
 * @param {Object} router - The router object for navigation.
 * @param {QueryParamBehavior} queryParamBehavior - The behavior for updating the query params. Default is TOGGLE.
 * @returns {void}
 */
export const updateFiltersInUrlParams = ({
  facetName,
  facetOption,
  router,
  queryParamBehavior = QueryParamBehavior.TOGGLE,
}) => {
  const [aspathname, query] = router.asPath.split('?');
  const newQueryParamString = getNewQueryParamString({
    query,
    newQueryParams: { [facetName]: facetOption },
    queryParamBehavior,
  });

  router.push(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

/**
 * Clears all CIO facets from the URL params, preserving other query params.
 * This is used when the user clears all filters.
 *
 * @param {Object} router - The router object for navigation.
 * @param {Array} allCioFacetNames - An array of all CIO facet names to be cleared.
 * @returns {void}
 */
export const clearAllCioFacetsFromUrlParams = ({
  router,
  allCioFacetNames,
}) => {
  const [aspathname, query] = router.asPath.split('?');
  const urlParams = new URLSearchParams(query);

  // Only remove cioFacets from query params
  // Preserve other query params like tabId and utm params
  const preservedQueryParams = {};
  urlParams.forEach((value, key) => {
    const mappedKey = urlFiltersToCioFacets[key] || key;

    if (!allCioFacetNames.includes(mappedKey)) {
      preservedQueryParams[mappedKey] = value;
    }
  });

  const newQueryParamString = new URLSearchParams(
    preservedQueryParams
  ).toString();

  router.push(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

/**
 * Returns the display name of the selected facet option.
 * If the selected facet is a size facet, it appends the selected facet display name.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.selectedFacetName - The name of the selected facet.
 * @param {string} params.selectedFacetDisplayName - The display name of the selected facet.
 * @param {string} params.selectedFacetOptionDisplayName - The display name of the selected facet option.
 * @returns {string} - The transformed display name of the selected facet option.
 *
 * @example
 * facetName          | displayName
 * -------------------|-----------------
 * available_size_top | S | 6 (Top Size)
 * tag_color_list     | Red
 */
export const getTransformedFacetOptionDisplayName = ({
  selectedFacetName,
  selectedFacetDisplayName,
  selectedFacetOptionDisplayName,
}) => {
  if (isCioSizeFacet(selectedFacetName)) {
    return `${selectedFacetOptionDisplayName} (${selectedFacetDisplayName})`;
  }

  return selectedFacetOptionDisplayName;
};

export const isolateSizeFacets = (cioFacets = []) => {
  const sizeFacets = [];
  const nonSizeFacets = [];

  cioFacets.forEach((facet) => {
    if (isCioSizeFacet(facet.name)) {
      sizeFacets.push(facet);
    } else {
      nonSizeFacets.push(facet);
    }
  });

  return { sizeFacets, nonSizeFacets };
};

/**
 * Group size facets together in the list of facets.
 * Size facets need to be grouped together for proper UI rendering and functionality.
 *
 * @param {Array} cioFacets - The list of facets to be grouped.
 * @returns {Array} - The list of facets with size facets grouped together.
 */
export const groupSizeFacets = (cioFacets = []) => {
  const firstSizeFacetIndex = cioFacets.findIndex((facet) =>
    isCioSizeFacet(facet.name)
  );

  const { sizeFacets, nonSizeFacets } = isolateSizeFacets(cioFacets);

  if (!sizeFacets.length) {
    return cioFacets;
  }

  const groupedSizeFacet = {
    display_name: 'Size', // needs to be localized via RB?
    sub_facets: sizeFacets,
  };

  return nonSizeFacets?.toSpliced(firstSizeFacetIndex, 0, groupedSizeFacet);
};

/**
 *  We can't use the `selected` directly from cioFacets from the CIO API response, due to size filters
 *  being in cio prefilter expressions instead of the filters param. This results in the size filters
 *  not having the selected value in the response. We have to handle selected filters
 *  by checking the URL params as our source of truth.
 *
 * @param {string} facetName - The name of the facet.
 * @param {string} facetOption - The value of the facet option.
 * @param {Object} selectedUrlFilters - The current URL filters.
 *
 * @returns {boolean} - Returns true if the facet option is selected, false otherwise.
 */
export const isFacetOptionSelected = ({
  facetName,
  facetOption,
  selectedUrlFilters,
}) => {
  if (!selectedUrlFilters || !facetName || !facetOption) {
    return false;
  }

  const urlFacetName = cioFacetsToUrlFilters[facetName] || facetName;
  return Boolean(selectedUrlFilters?.[urlFacetName]?.includes(facetOption));
};
